import {Component, EventEmitter, HostListener, Input, OnInit, Output} from '@angular/core';
import {AuthService} from '../../../auth/services/auth/auth.service';
import {PermissionService} from '../../../permission/services/permission/permission.service';
import {Title} from '@angular/platform-browser';
import {Tab} from '../../../tabbing/data/tab';
import {TooltipService} from '../../../tooltip/services/tooltip.service';
import packageInfo from '../../../../../package.json';
import buildFile from '../../../../../buildNumber.json';
import {BaseEntityEnum, CrudOperationEnum} from '../../../activity-log/data/operationTypeEnum';
import {UserService} from '../../../user/services/user/user.service';
import {TenantService} from '../../../auth/services/tenant/tenant.service';
import {AppointmentService} from '../../../appointment/services/appointment.service';
import {NavigationService} from '../../services/navigation.service';
import {PlatformService} from '../../services/native/platform.service';
import {NativeService} from '../../services/native/native.service';
import {LoggerService} from '../../../logging/services/logger.service';
import {LogLevel} from '../../../logging/data/logLevel';
import {LogSourceType} from '../../../logging/data/logSourceType';
import {LogSource} from '../../../logging/data/logSource';
import {environment} from '../../../../environments/environment';

@Component({
  selector: 'app-side-nav',
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.scss']
})
export class SideNavComponent implements OnInit {

  @Input() tab: Tab;
  @Output() pageChange: EventEmitter<number> = new EventEmitter<number>();
  @Output() extendedChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() extended = false;

  pages: string[];
  icons: string[];
  routes: string[];
  tenantName: string;

  frontEndVersion: string = packageInfo.version;
  frontEndBuildNumber: string = buildFile.buildNumber;
  frontEndBuildBranch: string = buildFile.branch;
  desktopWrapperVersionAndBuildNumber: string = '';

  state = {
    extended: false
  };
  exitContainerOpen: boolean;
  settingsOpen: boolean = false;
  windowWidth: number;

  private exitContainerLife: number;
  private keepExitContainerAlive: boolean;
  isDev = environment.production === false;

  constructor(private navigationService: NavigationService,
              private authService: AuthService,
              private permissionService: PermissionService,
              private titleService: Title,
              public platformService: PlatformService,
              private nativeService: NativeService,
              private tooltipService: TooltipService,
              public userService: UserService,
              private tenantService: TenantService,
              private appointmentService: AppointmentService,
              private loggerService: LoggerService) {
  }

  async ngOnInit() {
    this.state.extended = localStorage.getItem('state.extended') === 'true';
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.COMPONENT, LogSource.SideNavComponent, 'Initializing side nav');
    this.getTenantName();

    this.pages = [];
    this.icons = [];
    this.routes = [];

    await this.buildLinks();
    await this.userService.fetchCurrentUser();

    if (this.platformService.isElectronApp) {
      const promise = await this.nativeService.getVersion();
      const {version, buildNumber} = promise;
      this.desktopWrapperVersionAndBuildNumber = `v${version} (${buildNumber})`;
    }

    console.log('state.extended', this.state.extended);

    setInterval(() => {
      if (this.keepExitContainerAlive) {
        return;
      }
      this.exitContainerLife -= 100;
      if (this.exitContainerLife <= 0) {
        this.closeExitContainer();
      }
    } , 100);
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.COMPONENT, LogSource.SideNavComponent, 'Initialized side nav');
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.windowWidth = document.body.getBoundingClientRect().width;
    if (this.windowWidth < 1920){
      this.state.extended = false;
      this.extended = false;
      this.extendedChange.emit(this.state.extended);
      localStorage.setItem('state.extended', this.state.extended.toString());
    }

    if (this.windowWidth >= 1920){
      this.state.extended = true;
      this.extended = false;
      this.extendedChange.emit(this.state.extended);
      localStorage.setItem('state.extended', this.state.extended.toString());
    }
  }

  getTenantName(): void {
    this.tenantName = this.tenantService.tenantDto.tenant.tenantName;
  }

  highlighted(i: number){
    return this.navigationService.currentRoute.url ===  `/${this.routes[i]}`;
  }

  showToolTip(page: string){
    if (!this.extended && page){
      return page;
    }
    return '';
  }

  async buildLinks() {
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.COMPONENT, LogSource.SideNavComponent, 'Building side nav links');
    this.pages = [];
    this.icons = [];
    this.routes = [];

    this.pages.push('Home');
    this.icons.push('fas fa-home');
    this.routes.push('home');

    if (await this.permissionService.hasPermission(BaseEntityEnum.Communication, CrudOperationEnum.Read)) {
      this.pages.push('Communications');
      this.icons.push('fas fa-phone');
      this.routes.push('communications');
    }

    if (await this.permissionService.hasPermission(BaseEntityEnum.Job, CrudOperationEnum.Read)) {
      this.pages.push('Jobs');
      this.icons.push('fas fa-briefcase');
      this.routes.push('jobs');
    }

    if (await this.permissionService.hasPermission(BaseEntityEnum.Customer, CrudOperationEnum.Read)) {
      this.pages.push('Contacts');
      this.icons.push('fas fa-user-tie');
      this.routes.push('contacts');
    }

    if (await this.permissionService.hasPermission(BaseEntityEnum.Snippet, CrudOperationEnum.Read)) {
      this.pages.push('Snippets');
      this.icons.push('fas fa-cut');
      this.routes.push('snippets');
    }

    if (await this.permissionService.hasPermission(BaseEntityEnum.Post, CrudOperationEnum.Read)){
      this.pages.push('Docs');
      this.icons.push('fas fa-info');
      this.routes.push('docs');
    }

    if (await this.permissionService.hasPermission(BaseEntityEnum.Appointment, CrudOperationEnum.Read)) {
      this.pages.push('Appointments');
      this.icons.push('fas fa-calendar-check');
      this.routes.push('appointments');
    }

    if (this.frontEndBuildBranch === 'sandbox' || this.frontEndBuildNumber === 'development') {
      this.pages.push('Resources');
      this.icons.push('fas fa-play-circle');
      this.routes.push('resources');
    }

    if (this.frontEndBuildBranch === 'sandbox' || this.frontEndBuildNumber === 'development'){
      this.pages.push('Testing');
      this.icons.push('fas fa-check');
      this.routes.push('test');
    }

    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.COMPONENT, LogSource.SideNavComponent, 'Built side nav links');
  }

  changePage(index: number) {
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.COMPONENT, LogSource.SideNavComponent, 'Changing page to ' + this.routes[index]);
    this.navigationService.navigate(this.tab, [this.routes[index]]);
    delete this.tooltipService.externalTooltips;
    delete this.tooltipService.customerTooltips;
    delete this.tooltipService.flagEditors;
    this.tooltipService.externalTooltips = [];
    this.tooltipService.customerTooltips = [];
    this.tooltipService.flagEditors = [];
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.COMPONENT, LogSource.SideNavComponent, 'Changed page to ' + this.routes[index]);
  }

  changeExtended(){
    this.extended = !this.extended;
    this.state.extended = !this.state.extended;
    localStorage.setItem('state.extended', this.state.extended.toString());
    this.extendedChange.emit(this.state.extended);
  }

  openSettings(){
    this.settingsOpen = true;
  }

  openExitContainer() {
    this.exitContainerOpen = true;
    this.exitContainerLife = 1000;
  }
  closeExitContainer() {
    this.exitContainerLife = 0;
    this.exitContainerOpen = false;
    this.keepExitContainerAlive = false;
  }
  keepExitContainerOpen() {
    this.keepExitContainerAlive = true;
  }

  async logout() {
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.COMPONENT, LogSource.SideNavComponent, 'Logging out');
    this.authService.logout();
    this.tenantService.disconnect();
    this.appointmentService.triggerRefreshAppointment();
    this.platformService.reloadApp();
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.COMPONENT, LogSource.SideNavComponent, 'Logged out');
  }

  async exit() {
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.COMPONENT, LogSource.SideNavComponent, 'Exiting');
    this.platformService.requestApplicationExit();
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.COMPONENT, LogSource.SideNavComponent, 'Exited');
  }

  async changeOrganization() {
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.COMPONENT, LogSource.SideNavComponent, 'Changing organization');
    this.tenantService.disconnect();
    this.platformService.reloadApp();
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.COMPONENT, LogSource.SideNavComponent, 'Changed organization');
  }


  async goToSettings() {
    this.navigationService.navigate(this.tab, ['settings']);
  }


  openInvitationWebsite() {
    this.platformService.openInBrowser(`${environment.authFrontendUrl}/organization/${this.tenantService.tenantDto.tenant.id}/members?action=invite`);
  }

  isAdmin() {
    return this.userService.isAdmin;
  }
}
